/**
 * Billing's API, typed at the edge of this package.
 *
 * Two clients, and the split mirrors the backend's for the same reason: the
 * customer's list has no company name on it, because there is only one company
 * and a field for it is a field that could one day carry another's.
 *
 * Every amount is a **string**. The API sends decimals as strings and this
 * package keeps them that way: `JSON.parse` turns 7357.25 into a binary float,
 * and a page that added a column of them would show a customer a total ending
 * in `.249999`. Formatting happens at the edge, in `core-i18n`'s `money`.
 */

import type { ApiClient } from "@hardauto/core-api";

const BACK_OFFICE = "/api/v1/billing";
const PORTAL = "/api/v1/portal/billing";

export type DocumentKind = "invoice" | "credit_note";

/** The five states the design's badges name. Derived, never stored. */
export type DocumentStatus = "paid" | "open" | "overdue" | "partial" | "credit";

export type PaymentMethod = "transfer" | "cheque" | "cash" | "card" | "credit_note" | "other";

export interface DocumentLine {
  position: number;
  reference: string;
  designation: string;
  sales_unit: string;
  quantity: string;
  unit_price: string;
  discount_pct: string;
  /** The document's own, not `quantity * unit_price` — see the backend. */
  amount: string;
}

export interface FinancialDocument {
  id: string;
  number: string;
  kind: DocumentKind;
  status: DocumentStatus;
  issued_on: string;
  due_on: string | null;
  subtotal: string;
  tax_amount: string;
  total: string;
  paid_amount: string;
  /** Signed: negative on a credit note, so a column of them sums correctly. */
  outstanding: string;
  /**
   * Something paid and something left.
   *
   * Carried separately from the status because a late invoice half paid
   * reports `overdue` — the actionable state — and a screen needs both facts.
   */
  partially_paid: boolean;
  currency: string;
  order_id: string | null;
  order_reference: string;
  site_code: string;
  has_pdf: boolean;
  lines: DocumentLine[];
}

export interface DocumentRow {
  document: FinancialDocument;
  client_id: string;
  client_code: string;
  client_name: string;
}

export interface DocumentList {
  items: DocumentRow[];
  total: number;
  next_cursor: string | null;
}

export interface PortalDocumentList {
  items: FinancialDocument[];
  total: number;
  next_cursor: string | null;
}

export interface AgeBucket {
  label: string;
  up_to_days: number | null;
  amount: string;
  count: number;
}

export interface Statement {
  client_code: string;
  company_name: string;
  credit_limit: string;
  balance: string;
  available: string;
  /** Whole percent. 0 when no limit is set, so a gauge never renders `NaN`. */
  used_pct: number;
  overdue: string;
  overdue_count: number;
  not_due: string;
  not_due_count: number;
  credits: string;
  credit_count: number;
  /** Money received against no particular document. */
  on_account: string;
  next_due_on: string | null;
  next_due_amount: string;
  ageing: AgeBucket[];
  as_of: string;
}

export interface Settlement {
  id: string;
  amount: string;
  received_on: string;
  method: PaymentMethod;
  document_id: string | null;
  document_number: string;
  reference: string;
}

/**
 * The back office's view of one document.
 *
 * Carries the customer and the provenance, which the shared document type
 * does not: the customer's own screen has no business knowing which connector
 * delivered their invoice, and the desk cannot record a payment without the
 * customer's id.
 */
export interface DocumentDetail {
  document: FinancialDocument;
  client_id: string;
  client_code: string;
  client_name: string;
  /** The connector's reference. Empty when this installation issued it. */
  external_ref: string;
  /** A download hands over the ERP's own file rather than a generated one. */
  erp_pdf: boolean;
  settlements: Settlement[];
}

/**
 * A list's filters.
 *
 * Every field admits `undefined` explicitly rather than merely being
 * optional: `exactOptionalPropertyTypes` is on, and the screens build this
 * object from state where "no filter" *is* `undefined`. Writing `kind?:
 * DocumentKind` would force a call site to assemble the object conditionally,
 * key by key, to say the same thing.
 */
export interface DocumentQuery {
  client_id?: string | undefined;
  kind?: DocumentKind | undefined;
  unpaid_only?: boolean | undefined;
  overdue_only?: boolean | undefined;
  search?: string | undefined;
  after_id?: string | undefined;
  limit?: number | undefined;
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) continue;
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : "";
}

/** The back office's ledger. */
export class BillingApi {
  readonly #client: ApiClient;

  constructor(client: ApiClient) {
    this.#client = client;
  }

  list(options: DocumentQuery = {}): Promise<DocumentList> {
    return this.#client.request<DocumentList>(
      `${BACK_OFFICE}/documents${query({ ...options, limit: options.limit ?? 50 })}`,
    );
  }

  get(id: string): Promise<DocumentDetail> {
    return this.#client.request<DocumentDetail>(
      `${BACK_OFFICE}/documents/${encodeURIComponent(id)}`,
    );
  }

  /**
   * The document as a blob.
   *
   * `requestBlob`, not a link: the route is behind a bearer token, and an
   * `<a href>` the browser follows carries no Authorization header — it would
   * download the sign-in page.
   */
  pdf(id: string): Promise<Blob> {
    return this.#client.requestBlob(`${BACK_OFFICE}/documents/${encodeURIComponent(id)}/pdf`);
  }

  issue(body: {
    kind: DocumentKind;
    client_id: string;
    issued_on: string;
    subtotal: string;
    tax_amount: string;
    total: string;
    due_on?: string | null;
    order_reference?: string;
    site_code?: string;
    currency?: string;
    lines?: {
      reference?: string;
      designation?: string;
      quantity?: string;
      unit_price?: string;
      amount?: string;
    }[];
  }): Promise<FinancialDocument> {
    return this.#client.request<FinancialDocument>(`${BACK_OFFICE}/documents`, {
      method: "POST",
      body,
    });
  }

  settle(body: {
    client_id: string;
    amount: string;
    received_on: string;
    method?: PaymentMethod;
    document_id?: string | null;
    reference?: string;
  }): Promise<Settlement> {
    return this.#client.request<Settlement>(`${BACK_OFFICE}/settlements`, {
      method: "POST",
      body,
    });
  }

  statement(clientId: string): Promise<Statement> {
    return this.#client.request<Statement>(
      `${BACK_OFFICE}/clients/${encodeURIComponent(clientId)}/statement`,
    );
  }

  /**
   * The statement as a file — the same one the customer can download.
   *
   * On this side because the request arrives by telephone: somebody asking
   * what they owe is asking a person, and that person needs the paper the
   * customer will read.
   */
  statementPdf(clientId: string): Promise<Blob> {
    return this.#client.requestBlob(
      `${BACK_OFFICE}/clients/${encodeURIComponent(clientId)}/statement/pdf`,
    );
  }

  refreshCredit(clientId: string): Promise<Statement> {
    return this.#client.request<Statement>(
      `${BACK_OFFICE}/clients/${encodeURIComponent(clientId)}/credit/refresh`,
      { method: "POST" },
    );
  }
}

/** A customer's own documents. */
export class PortalBillingApi {
  readonly #client: ApiClient;
  readonly #clientCode: string | undefined;

  /**
   * `clientCode` is the *only* thing a portal caller may influence, and the
   * server matches it against their own links — a code they are not linked to
   * is refused exactly as if they had none. It exists because a broker acting
   * for three companies should not need three accounts.
   */
  constructor(client: ApiClient, clientCode?: string) {
    this.#client = client;
    this.#clientCode = clientCode;
  }

  #query(params: Record<string, string | number | boolean | undefined> = {}): string {
    return query({ ...params, client_code: this.#clientCode });
  }

  list(options: Omit<DocumentQuery, "client_id"> = {}): Promise<PortalDocumentList> {
    return this.#client.request<PortalDocumentList>(
      `${PORTAL}/documents${this.#query({ ...options, limit: options.limit ?? 50 })}`,
    );
  }

  get(id: string): Promise<FinancialDocument> {
    return this.#client.request<FinancialDocument>(
      `${PORTAL}/documents/${encodeURIComponent(id)}${this.#query()}`,
    );
  }

  pdf(id: string): Promise<Blob> {
    return this.#client.requestBlob(
      `${PORTAL}/documents/${encodeURIComponent(id)}/pdf${this.#query()}`,
    );
  }

  statement(): Promise<Statement> {
    return this.#client.request<Statement>(`${PORTAL}/statement${this.#query()}`);
  }

  statementPdf(): Promise<Blob> {
    return this.#client.requestBlob(`${PORTAL}/statement/pdf${this.#query()}`);
  }

  settlements(limit = 50): Promise<{ items: Settlement[] }> {
    return this.#client.request<{ items: Settlement[] }>(
      `${PORTAL}/settlements${this.#query({ limit })}`,
    );
  }
}
